import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Worlds, errorMessage } from '../api/client';
import { AuthUser, ServerAction, TrustedPlayer, World, WorldBackup, WorldTab, canViewLogs, canViewPerf, canRunConsoleCommands, isAdminUser, isStaffUser } from '../types';
import { useServerAction } from '../hooks/useServerAction';
import { useNamesFor } from '../hooks/useUsernames';
import { PowerControls } from './PowerControls';
import { ShutdownCountdown } from './ShutdownCountdown';
import { Icon } from './ui/Icon';
import SettingsTab from './world/SettingsTab';
import BackupsTab from './world/BackupsTab';
import PlayersTab from './world/PlayersTab';
import ActivityTab from './world/ActivityTab';
import LogsTab from './world/LogsTab';
import PerfTab from './world/PerfTab';
import AdminTab from './world/AdminTab';
import MapTab from './world/MapTab';
import { genLabel, statusInfo, timeAgo } from '../utils/format';
import { getRegionFriendlyName } from '../utils/regionUtils';

interface Props {
  world: World;
  me: AuthUser;
  onWorldsRefresh: () => void;
}

const LIVE_REFRESH_MS = 15000;

/** Legacy tab names from the old site still resolve. */
const TAB_ALIASES: Record<string, WorldTab> = {
  settings: 'settings',
  properties: 'settings',
  backups: 'backups',
  players: 'players',
  trusted: 'players',
  activity: 'activity',
  audits: 'activity',
  logs: 'logs',
  perf: 'perf',
  performance: 'perf',
  stats: 'perf',
  admin: 'admin',
  map: 'map',
};

const WorldPage: React.FC<Props> = ({ world, me, onWorldsRefresh }) => {
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const staff = isStaffUser(me);
  const logsAllowed = canViewLogs(me);
  const perfAllowed = canViewPerf(me);
  // Admin and Developer roles only (not Staff): the Admin tab and its toggles.
  const admin = isAdminUser(me);
  // Developer role only (not admins): the console command box under the live server log.
  const consoleAllowed = canRunConsoleCommands(me);
  const isOwn = !!me.minecraftUuid && world.ownerId === me.minecraftUuid;

  const tabs = useMemo(() => {
    const list: { id: WorldTab; label: string }[] = [
      { id: 'settings', label: 'Settings' },
      { id: 'backups', label: 'Backups' },
      { id: 'players', label: 'Players' },
      { id: 'activity', label: 'Activity' },
    ];
    if (logsAllowed) list.push({ id: 'logs', label: 'Server log' });
    if (perfAllowed) list.push({ id: 'perf', label: 'Performance' });
    if (admin) list.push({ id: 'admin', label: 'Admin' });
    list.push({ id: 'map', label: 'Map' });
    return list;
  }, [logsAllowed, perfAllowed, admin]);

  const resolved = tabParam ? TAB_ALIASES[tabParam.toLowerCase()] : undefined;
  const tab: WorldTab = resolved && tabs.some((t) => t.id === resolved) ? resolved : 'settings';

  // Normalise the URL (missing / legacy / disallowed tab → canonical).
  useEffect(() => {
    if (tabParam !== tab) navigate(`/worlds/${world.id}/${tab}`, { replace: true });
  }, [tabParam, tab, world.id, navigate]);

  // Backups and trusted players are loaded here so the hero can summarise them and the tabs can reuse them.
  const [backups, setBackups] = useState<WorldBackup[]>([]);
  const [backupsLoading, setBackupsLoading] = useState(true);
  const [backupsError, setBackupsError] = useState<string | null>(null);
  const [trusted, setTrusted] = useState<TrustedPlayer[] | null>(null);
  const [trustedError, setTrustedError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadBackups = useCallback(async () => {
    try {
      setBackups(await Worlds.backups(world.id));
      setBackupsError(null);
    } catch (e) {
      setBackupsError(errorMessage(e, 'Failed to load backups.'));
    } finally {
      setBackupsLoading(false);
    }
  }, [world.id]);

  const loadTrusted = useCallback(async () => {
    try {
      setTrusted(await Worlds.trusted(world.id));
      setTrustedError(null);
    } catch (e) {
      setTrustedError(errorMessage(e, 'Failed to load trusted players.'));
    }
  }, [world.id]);

  useEffect(() => {
    setBackups([]);
    setBackupsLoading(true);
    setTrusted(null);
    loadBackups();
    loadTrusted();
  }, [loadBackups, loadTrusted]);

  // Background refresh when the tab regains focus.
  useEffect(() => {
    const onFocus = () => {
      loadBackups();
      loadTrusted();
      setRefreshKey((k) => k + 1);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadBackups, loadTrusted]);

  const latestBackup = useMemo(
    () => backups.reduce<WorldBackup | null>((acc, b) => (!acc || new Date(b.lastModified) > new Date(acc.lastModified) ? b : acc), null),
    [backups]
  );

  const nameOf = useNamesFor(isOwn ? [] : [world.ownerId]);
  const onActionDone = useCallback(
    (_w: World, _a: ServerAction) => {
      // Stop / kill change the status synchronously; a start is picked up by the control
      // service a moment later (status flips to LOADING), so refresh again after a short delay.
      onWorldsRefresh();
      window.setTimeout(onWorldsRefresh, 2000);
    },
    [onWorldsRefresh]
  );
  const { request, modal, busyFor } = useServerAction(onActionDone);
  // Marking a backup on a running world restarts it asynchronously, so refresh now (the mark) and again
  // once the control service has flipped the status to RESTORING.
  const onBackupsChanged = useCallback(() => {
    onWorldsRefresh();
    window.setTimeout(onWorldsRefresh, 2500);
  }, [onWorldsRefresh]);
  const st = statusInfo(world.status);
  const trustedCount = trusted ? trusted.length : null;

  // While the world is running or in transition, poll the worlds list so the status, and the
  // automatic-shutdown countdown the plugin publishes, stay current without a page focus.
  const live = st.active || st.busy;
  useEffect(() => {
    if (!live) return;
    const t = window.setInterval(onWorldsRefresh, LIVE_REFRESH_MS);
    return () => window.clearInterval(t);
  }, [live, onWorldsRefresh]);

  return (
    <div className="main2">
      <header className="hero">
        <div className="hero-in">
          <div className="hero-top">
            {(isOwn || staff) && <PowerControls world={world} onRequest={request} busy={busyFor(world.id)} canStart canManage={staff} />}
          </div>
          <div className="hero-id">
            <div style={{ minWidth: 0 }}>
              <h1 className="hero-name">{world.name}</h1>
              <div className="hero-meta">
                <span className={`pill ${st.pill}`}>
                  <span className={`dot ${st.dot}`} />
                  {st.text}
                </span>
                <ShutdownCountdown world={world} />
                {!isOwn && staff && <span className="pill">Staff · viewing</span>}
                {world.isTesting && <span className="pill">testing</span>}
                {world.lastUsedVersion && (
                  <>
                    <span className="hero-fact">
                      <b>{world.lastUsedVersion}</b>
                    </span>
                    <span className="hero-sep" />
                  </>
                )}
                <span className="hero-fact">{genLabel(world)}</span>
                <span className="hero-sep" />
                <span className="hero-fact">{getRegionFriendlyName(world.region)}</span>
                {latestBackup && (
                  <>
                    <span className="hero-sep" />
                    <span className="hero-fact">backed up {timeAgo(latestBackup.lastModified)}</span>
                  </>
                )}
                {trustedCount != null && (
                  <>
                    <span className="hero-sep" />
                    <span className="hero-fact">
                      <b>{trustedCount}</b> trusted
                    </span>
                  </>
                )}
                {world.lastAccessedAt && (
                  <>
                    <span className="hero-sep" />
                    <span className="hero-fact">played {timeAgo(world.lastAccessedAt)}</span>
                  </>
                )}
                <span className="hero-sep" />
                <span className="hero-fact">
                  owner <b>{isOwn ? 'you' : nameOf(world.ownerId)}</b>
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>
      <nav className="wnav">
        <div className="wnav-in" role="tablist">
          {tabs.map((t) => (
            <button key={t.id} type="button" className="wtab" role="tab" aria-selected={tab === t.id} onClick={() => navigate(`/worlds/${world.id}/${t.id}`)}>
              {t.label}
            </button>
          ))}
        </div>
      </nav>
      <div className="page">
        {tab === 'settings' && <SettingsTab world={world} refreshKey={refreshKey} onWorldChanged={onWorldsRefresh} />}
        {tab === 'backups' && (
          <BackupsTab world={world} backups={backups} loading={backupsLoading} error={backupsError} onUploaded={loadBackups} onWorldChanged={onBackupsChanged} />
        )}
        {tab === 'players' && <PlayersTab world={world} trusted={trusted} error={trustedError} onChange={setTrusted} />}
        {tab === 'activity' && <ActivityTab world={world} refreshKey={refreshKey} />}
        {tab === 'logs' && logsAllowed && <LogsTab world={world} consoleAllowed={consoleAllowed} />}
        {tab === 'perf' && perfAllowed && <PerfTab world={world} />}
        {tab === 'admin' && admin && <AdminTab world={world} onWorldChanged={onWorldsRefresh} />}
        {tab === 'map' && <MapTab world={world} />}
        {st.key === 'error' && tab === 'settings' && (
          <div className="note note--danger" style={{ marginTop: 24 }}>
            <Icon name="warn" size={15} />
            <span>This world is in an error state. Ask staff in Discord if it doesn't recover after the next start.</span>
          </div>
        )}
      </div>
      {modal}
    </div>
  );
};

export default WorldPage;
